import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, ExternalLink, Camera, User, Sparkles } from 'lucide-react';
import { Button } from '../components/ui/button';

// Magazine detail data with BTS and model info
const magazineDetails = {
  '95': {
    title: 'I DO Summer 2026',
    date: 'Summer 2026 | Issue No 95 | €2.50',
    cover: '/issue-cover-summer-2026.jpg',
    externalLink: 'https://www.independent.com.mt/articles/2026-07-09/magazine-ido/I-DO-June-2026-6736291222',
    description: 'Candyland Romance, our Spring/Summer 2026 issue is a love letter to playful elegance. Pastel skies, sugared-almond palettes, dreamy seaside ceremonies, and love that feels like a daydream. Available now in print across Malta and free to read online.',
    coverModel: {
      name: 'Candyland Romance',
      bio: 'Our Summer 2026 cover captures a sun-drenched seaside ceremony, soft pinks, blush bouquets, and the effortless romance of a Mediterranean wedding at golden hour.',
      credits: [
        { role: 'Photography', name: 'I DO Editorial' },
        { role: 'Styling', name: 'I DO Creative Team' },
      ]
    },
    behindTheScenes: {
      description: 'Bringing Candyland Romance to life meant chasing soft light along Malta\'s coastline, pastel florals, candy-toned styling, and a celebration of the season\'s most romantic trends.',
      highlights: [
        'Seaside ceremony styling shot at golden hour',
        'Soft pastel florals and candy-inspired colour palettes',
        'Collaboration with Malta\'s leading wedding vendors',
        'Romantic soft-girl beauty looks created on location'
      ]
    },
    insideThisIssue: [
      'The New Era of Bridesmaids: Style, shape & individuality',
      'The Wedding Breakfast, Reinvented',
      'The Soft Girl Bride: Romantic makeup trends taking over 2026',
      'Honeymoon, Reimagined: The art of the first escape',
      'Wedding Chaos: How to stay sane when nothing goes to plan',
      'Beyond the Dance Floor: The new rules of wedding fun'
    ]
  },
  '1': {
    title: 'I DO November 2025',
    date: 'December 2025',
    cover: 'https://www.independent.com.mt/file.aspx?f=257177&width=400&height=600',
    externalLink: 'https://www.independent.com.mt/articles/2025-12-11/magazine-ido/I-DO-November-2025-6736285553',
    description: 'Our November 2025 issue celebrates the magic of winter weddings in Malta. Featuring real love stories, bridal fashion, stunning venues, and expert advice from Malta\'s top wedding professionals.',
    coverModel: {
      name: 'Featured Cover Look',
      bio: 'This issue\'s cover was styled to capture the essence of modern Maltese bridal elegance, soft, romantic, and effortlessly chic.',
      credits: [
        { role: 'Photography', name: 'Siemenjov Galdes Weddings', link: 'https://www.siemenjovgaldes.com' },
        { role: 'Styling', name: 'I DO Editorial Team' },
      ]
    },
    behindTheScenes: {
      description: 'A glimpse into the creative process behind our November 2025 editorial shoot. From location scouting across Malta\'s most iconic venues to final styling details, every element was carefully curated to bring this issue to life.',
      highlights: [
        'Shot across multiple locations including historic Valletta and countryside estates',
        'Collaboration with over 10 of Malta\'s leading wedding vendors',
        'Three days of shooting to capture the perfect golden-hour moments',
        'Custom floral arrangements created specifically for the editorial'
      ]
    },
    insideThisIssue: [
      'Real Wedding: Rebecca & Nicolas, a Maltese love story',
      'Bridal Fashion: Winter wedding dress trends',
      'Venue Guide: Malta\'s most romantic winter settings',
      'Beauty: Seasonal bridal looks from Malta\'s top artists',
      'Planning: Expert tips for winter celebrations'
    ]
  },
  '2': {
    title: 'I DO August 2025',
    date: 'October 2025',
    cover: 'https://www.independent.com.mt/file.aspx?f=254968&width=400&height=600',
    externalLink: 'https://www.independent.com.mt/articles/2025-10-22/magazine-ido/I-DO-August-2025-6736284167',
    description: 'Our August 2025 issue is a celebration of summer love, sun-drenched ceremonies, al fresco receptions, and the vibrant energy of Malta\'s wedding season at its peak.',
    coverModel: {
      name: 'Summer Editorial',
      bio: 'A sun-kissed editorial capturing the joy and freedom of summer weddings in the Mediterranean.',
      credits: [
        { role: 'Photography', name: 'I DO Editorial' },
        { role: 'Styling', name: 'I DO Creative Team' },
      ]
    },
    behindTheScenes: {
      description: 'Behind the scenes of our summer editorial, early morning starts to catch the light, seaside styling sessions, and the magic of shooting against Malta\'s stunning coastline.',
      highlights: [
        'Golden hour shoots along the Mediterranean coast',
        'Fresh summer floral designs and tablescaping',
        'Collaboration with emerging Maltese bridal designers',
        'Outdoor styling in Malta\'s most beautiful garden venues'
      ]
    },
    insideThisIssue: [
      'Real Wedding: Andrea & Alex, a Hallowedding love story',
      'Summer Bridal Fashion: Light, breezy, and beautiful',
      'Venue Spotlight: Open-air venues for summer celebrations',
      'Beauty: Sun-proof bridal makeup that lasts all day',
      'Inspiration: Mediterranean colour palettes for your big day'
    ]
  },
  '3': {
    title: 'I DO February 2025',
    date: 'February 2025',
    cover: 'https://www.independent.com.mt/file.aspx?f=248640&width=400&height=600',
    externalLink: 'https://www.independent.com.mt/articles/2025-02-11/magazine-ido/I-DO-February-2025-6736267790',
    description: 'Our February 2025 Valentine\'s edition is dedicated to romance in every form, from intimate elopements to grand celebrations, this issue is filled with love and inspiration.',
    coverModel: {
      name: 'Valentine\'s Editorial',
      bio: 'Romantic, elegant, and timeless, our Valentine\'s cover embodies the spirit of love that I DO celebrates in every issue.',
      credits: [
        { role: 'Photography', name: 'I DO Editorial' },
        { role: 'Styling', name: 'I DO Creative Team' },
      ]
    },
    behindTheScenes: {
      description: 'Creating the Valentine\'s issue meant diving deep into what makes love stories so beautiful. From candlelit setups to delicate floral installations, every detail spoke of romance.',
      highlights: [
        'Romantic indoor shoots with dramatic candlelight',
        'Vintage-inspired styling with modern touches',
        'Intimate venue settings across Malta and Gozo',
        'Collaboration with Malta\'s finest jewellers and florists'
      ]
    },
    insideThisIssue: [
      'Real Wedding: Kelly & Matthew, eleven years of love',
      'Valentine\'s Bridal Fashion: Romantic gowns and accessories',
      'Jewellery Guide: Rings that tell your story',
      'Planning: Intimate wedding ideas for two',
      'Expert Advice: Making the most of your engagement'
    ]
  }
};

const MagazineDetailPage = () => {
  const { id } = useParams();
  const magazine = magazineDetails[id];

  if (!magazine) {
    return (
      <div className="min-h-screen bg-[#FAF9F7] flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl text-[#2C2C2C] mb-4" style={{ fontFamily: '"Playfair Display", serif' }}>Issue Not Found</h2>
          <p className="text-[#757B7F] mb-6">This issue doesn't have a dedicated page yet.</p>
          <Link to="/the-magazine" className="text-[#DE92A9] underline">Back to all issues</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FAF9F7]">
      {/* Hero */}
      <section className="relative">
        <div className="relative h-[300px] md:h-[400px] overflow-hidden">
          <img
            src="https://images.unsplash.com/photo-1522673607200-164d1b6ce486?w=1600&h=800&fit=crop"
            alt={magazine.title}
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-[#FAF9F7]"></div>
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="text-center px-4">
              <p className="text-white/90 text-xs tracking-[0.3em] uppercase mb-4">
                {magazine.date}
              </p>
              <h1
                className="text-4xl md:text-6xl text-white mb-4 tracking-tight drop-shadow-lg"
                style={{ fontFamily: '"Playfair Display", serif' }}
              >
                {magazine.title}
              </h1>
            </div>
          </div>
        </div>
      </section>

      {/* Back Link */}
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pt-6">
        <Link to="/the-magazine" className="inline-flex items-center gap-2 text-[#DE92A9] text-sm hover:gap-3 transition-all">
          <ArrowLeft size={16} />
          Back to all issues
        </Link>
      </div>

      {/* Cover & Description */}
      <section className="py-12">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-3 gap-10">
            {/* Cover Image */}
            <div className="md:col-span-1">
              <div className="sticky top-32">
                <img
                  src={magazine.cover}
                  alt={magazine.title}
                  className="w-full rounded-xl shadow-2xl"
                />
                <a
                  href={magazine.externalLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mt-6 w-full inline-flex items-center justify-center gap-2 bg-[#DE92A9] hover:bg-[#C9758F] text-white py-4 rounded-lg text-sm tracking-wider transition-colors"
                  data-testid="read-magazine-btn"
                >
                  <ExternalLink size={16} />
                  READ FULL MAGAZINE
                </a>
              </div>
            </div>

            {/* Content */}
            <div className="md:col-span-2 space-y-12">
              {/* About This Issue */}
              <div>
                <h2
                  className="text-3xl text-[#2C2C2C] mb-4"
                  style={{ fontFamily: '"Playfair Display", serif' }}
                >
                  About This Issue
                </h2>
                <div className="w-16 h-0.5 mb-6" style={{ backgroundColor: '#DE92A9' }}></div>
                <p className="text-[#757B7F] text-base leading-relaxed">{magazine.description}</p>
              </div>

              {/* Inside This Issue */}
              <div>
                <h2
                  className="text-3xl text-[#2C2C2C] mb-4"
                  style={{ fontFamily: '"Playfair Display", serif' }}
                >
                  Inside This Issue
                </h2>
                <div className="w-16 h-0.5 mb-6" style={{ backgroundColor: '#DE92A9' }}></div>
                <ul className="space-y-3">
                  {magazine.insideThisIssue.map((item) => (
                    <li key={item} className="flex items-start gap-3">
                      <Sparkles size={16} className="mt-1 flex-shrink-0" style={{ color: '#DE92A9' }} />
                      <span className="text-[#2C2C2C] text-sm">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Meet the Model / Cover Story */}
              <div className="bg-white rounded-xl p-8 border border-[#E5E0DA] shadow-sm">
                <div className="flex items-center gap-3 mb-4">
                  <User size={24} style={{ color: '#DE92A9' }} />
                  <h2
                    className="text-3xl text-[#2C2C2C]"
                    style={{ fontFamily: '"Playfair Display", serif' }}
                  >
                    Cover Story
                  </h2>
                </div>
                <div className="w-16 h-0.5 mb-6" style={{ backgroundColor: '#DE92A9' }}></div>
                <h3 className="text-lg font-semibold text-[#2C2C2C] mb-2">{magazine.coverModel.name}</h3>
                <p className="text-[#757B7F] text-sm leading-relaxed mb-6">{magazine.coverModel.bio}</p>
                <div className="space-y-2">
                  {magazine.coverModel.credits.map((credit) => (
                    <div key={`${credit.role}-${credit.name}`} className="flex items-center gap-4 text-sm">
                      <span className="text-[#757B7F] w-28">{credit.role}</span>
                      {credit.link ? (
                        <a href={credit.link} target="_blank" rel="noopener noreferrer" className="text-[#DE92A9] font-medium hover:underline">
                          {credit.name}
                        </a>
                      ) : (
                        <span className="text-[#2C2C2C] font-medium">{credit.name}</span>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              {/* Behind the Scenes */}
              <div className="bg-white rounded-xl p-8 border border-[#E5E0DA] shadow-sm">
                <div className="flex items-center gap-3 mb-4">
                  <Camera size={24} style={{ color: '#DE92A9' }} />
                  <h2
                    className="text-3xl text-[#2C2C2C]"
                    style={{ fontFamily: '"Playfair Display", serif' }}
                  >
                    Behind the Scenes
                  </h2>
                </div>
                <div className="w-16 h-0.5 mb-6" style={{ backgroundColor: '#DE92A9' }}></div>
                <p className="text-[#757B7F] text-base leading-relaxed mb-6">{magazine.behindTheScenes.description}</p>
                <div className="grid md:grid-cols-2 gap-3">
                  {magazine.behindTheScenes.highlights.map((highlight) => (
                    <div key={highlight} className="bg-[#FAF9F7] rounded-lg p-4 border border-[#E5E0DA]">
                      <p className="text-sm text-[#2C2C2C]">{highlight}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2
            className="text-3xl text-[#2C2C2C] mb-6"
            style={{ fontFamily: '"Playfair Display", serif' }}
          >
            Want to be in our next issue?
          </h2>
          <p className="text-[#757B7F] mb-8">Share your wedding story or advertise with us.</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/submit-wedding">
              <Button className="bg-[#DE92A9] hover:bg-[#C9758F] text-white px-8 py-4 text-sm tracking-wider">
                SUBMIT YOUR STORY
              </Button>
            </Link>
            <Link to="/advertise">
              <Button variant="outline" className="border-[#DE92A9] text-[#DE92A9] hover:bg-[#DE92A9] hover:text-white px-8 py-4 text-sm tracking-wider">
                ADVERTISE WITH US
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export { magazineDetails };
export default MagazineDetailPage;
